import React from "react";
import type { ApolloClient } from "@apollo/client";
import ProgressBar from "components/ProgressBar";
import { Col, Form, Row } from "react-bootstrap";
import { isEmpty } from "lodash";
import TermsModal from "../TermsModal";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import {
  getUserProfile,
  getUserState,
  updateEmployerAccountTermsAndSubmit,
} from "actions/userActions";
import { getCompanyInfo, getPaymentRequirement } from "actions/employerActions";
import Button from "components/Button";
import Alert from "components/Alert";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { userConstants } from "actions/types";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import {
  showsOrderBillingStepSelector,
  isIsolvedWholesale,
  planCodeSelector,
} from "store/selectors/employer";
import PlanCard from "./PlanCard";
import IconSpinner from "components/IconSpinner";
import { ENTERPRISE, version1Keys } from "statics/planCodes";
import { AGREEMENTS } from "statics/onboardingSteps";
import { userEmail, userProfile } from "store/selectors/user";
import EmployerAdminNameForm from "components/EmployerAdminNameForm";
import moment from "moment";
import type { RootState } from "types/state";

const actions = [
  userConstants.USER_EMPLOYER_TERMS_UPDATE,
  userConstants.USER_STATE,
];

const loadingSelector = createLoadingSelector(actions);

const errorSelector = createErrorSelector(actions);

interface EmployerTermsUserProfile {
  firstName?: string;
  lastName?: string;
  [key: string]: unknown;
}

const mapStateToProps = (state: RootState) => ({
  error: errorSelector(state),
  isFetching: loadingSelector(state),
  isMultiAccount: state.employer.isMultiAccount,
  // planCodeSelector returns string | null at runtime; consumers (regex.test,
  // version1Keys.includes, PlanCard) treat it as a string — cast at the boundary
  planCode: planCodeSelector(state) as string,
  // selectors/user.js userProfile is loosely typed — cast at the boundary
  userProfile: userProfile(state) as EmployerTermsUserProfile,
  userEmail: userEmail(state),
  isIsolvedWholesale: isIsolvedWholesale(state),
  showsOrderBillingStep: showsOrderBillingStepSelector(state),
});

const mapDispatchToProps = {
  getUserState,
  getCompanyInfo,
  getPaymentRequirement,
  updateEmployerAccountTermsAndSubmit,
  getUserProfile,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  termsOfService: boolean | string;
  termsOfOrder: boolean | string;
  crbAgreement: boolean | string;
  finalAgreement: boolean | string;
  submitted: boolean;
  showTermsOfService: boolean;
  showTermsOfOrder: boolean;
  loading: boolean;
  fetchingProfile: boolean;
  showCRBAgreement: boolean;
}

class EmployerTerms extends React.Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      termsOfService: "",
      termsOfOrder: "",
      crbAgreement: "",
      finalAgreement: "",
      submitted: false,
      showTermsOfService: false,
      showTermsOfOrder: false,
      loading: false,
      fetchingProfile: true,
      showCRBAgreement: false,
    };
  }

  async componentDidMount() {
    window.analytics.page("Employer Terms");
    await Promise.all([
      this.props.getCompanyInfo(this.props.client as ApolloClient<object>),
      this.props.getUserProfile(this.props.client as ApolloClient<object>),
      this.props.getPaymentRequirement(
        this.props.client as ApolloClient<object>
      ),
    ]);

    this.setState({ fetchingProfile: false });

    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  _submitTerms = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let terms = {
      termsOfService: this.state.termsOfService,
      termsOfOrder: this.state.termsOfOrder,
    };

    void this.props
      .updateEmployerAccountTermsAndSubmit(
        this.props.client as ApolloClient<object>,
        // the mutation only sends termsOfService; termsOfOrder is preserved
        // verbatim from the original submit payload
        terms as Parameters<
          (typeof this.props)["updateEmployerAccountTermsAndSubmit"]
        >[1]
      )
      .then((result) => {
        if (!result.success) {
          return;
        }
        void this.props
          .getUserState(this.props.client as ApolloClient<object>)
          .then(() => this.props.push("/dashboard"));
      });

    window.analytics.track("Submitted Terms");

    if (this._isMounted) {
      this.setState({
        submitted: true,
      });
    }
  };

  _handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      [e.target.name]: e.target.checked,
    } as Pick<State, keyof State>);
  };

  _shouldShowTermsOfOrder = () => {
    const isSelfServe =
      this.props.planCode && this.props.planCode !== ENTERPRISE;
    const isIsolvedWholesale = this.props.isIsolvedWholesale;
    return isSelfServe || isIsolvedWholesale;
  };

  _calcButtonDisabled = () => {
    const { termsOfService, termsOfOrder, finalAgreement, crbAgreement } =
      this.state;
    const notAcceptedBasicAgreements =
      !termsOfService || !finalAgreement || !crbAgreement;

    // we only care about termsOfOrder if we are showing it
    if (this._shouldShowTermsOfOrder()) {
      return notAcceptedBasicAgreements || !termsOfOrder;
    }

    return notAcceptedBasicAgreements;
  };

  getOrderFormType = () => {
    if (this.props.isIsolvedWholesale) {
      return "isolvedOrder";
    }
    const versionToUse = version1Keys.includes(this.props.planCode)
      ? "employerOrder"
      : "employerOrderV2";

    return versionToUse;
  };

  render() {
    let content;
    const { firstName, lastName } = this.props.userProfile;
    if (this.state.fetchingProfile) {
      content = <IconSpinner centered />;
    } else if (isEmpty(firstName) || isEmpty(lastName)) {
      content = (
        // @ts-expect-error PRESERVED BUG (conversion-only): required onSuccess prop omitted in original JSX
        <EmployerAdminNameForm />
      );
    } else {
      content = this.renderForm();
    }
    const showBillingRouteAndTermsOfOrder = this._shouldShowTermsOfOrder();

    return (
      <div id="employer-terms">
        <ProgressBar
          isEmployer={true}
          activeStepId={AGREEMENTS.id}
          progressPercent={"90"}
          isMultiAccount={this.props.isMultiAccount}
          showBillingRoute={
            showBillingRouteAndTermsOfOrder || this.props.showsOrderBillingStep
          }
        />
        {content}
      </div>
    );
  }

  renderForm = () => {
    const showBillingRouteAndTermsOfOrder = this._shouldShowTermsOfOrder();

    const termsInfo = {
      ...this.props.userProfile,
      userEmail: this.props.userEmail,
      effectiveDate: moment().format("MMMM D, YYYY"),
    };

    return (
      <div id="employer-terms">
        <TermsModal
          termsInfo={termsInfo}
          type="employerAgreement"
          show={this.state.showTermsOfService}
          onClose={() => this.setState({ showTermsOfService: false })}
        />
        <TermsModal
          termsInfo={termsInfo}
          type={this.getOrderFormType()}
          show={this.state.showTermsOfOrder}
          onClose={() => this.setState({ showTermsOfOrder: false })}
        />
        <TermsModal
          termsInfo={termsInfo}
          type="crbAgreement"
          show={this.state.showCRBAgreement}
          onClose={() => this.setState({ showCRBAgreement: false })}
        />

        <Form noValidate onSubmit={this._submitTerms}>
          <div className="mega-container">
            <div className="step-container is-active" data-circle-percent="100">
              <section className="form-sec-2col">
                <section className="page-title-wrap">
                  <article className="text-cell">
                    <h1 className="page-title">Agreements</h1>
                    <p className="page-subtext">
                      Please review and acknowledge our{" "}
                      {showBillingRouteAndTermsOfOrder && " Order Form and"}{" "}
                      Platform Services Agreement.
                    </p>

                    <div id="form-employer-company-profile">
                      <Row className="mb-3">
                        <Form.Group
                          as={Col}
                          sm={12}
                          controlId="formBasictermsOfService"
                        >
                          <Form.Check
                            type="checkbox"
                            id="termsOfService"
                            name="termsOfService"
                            label="Platform Services Agreement"
                            value={this.state.termsOfService as string}
                            onChange={this._handleCheckBoxChange}
                          />
                          <div
                            className="modal-link"
                            onClick={() => {
                              this.setState({
                                showTermsOfService: true,
                              });
                              window.analytics.track(
                                "Viewed Platform Services Agreement"
                              );
                            }}
                          >
                            (View Agreement)
                          </div>
                        </Form.Group>
                      </Row>
                      {showBillingRouteAndTermsOfOrder && (
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            sm={12}
                            controlId="formBasictermsOfOrder"
                          >
                            <Form.Check
                              type="checkbox"
                              id="termsOfOrder"
                              name="termsOfOrder"
                              label="Order Form"
                              value={this.state.termsOfOrder as string}
                              onChange={this._handleCheckBoxChange}
                            />
                            <div
                              className="modal-link"
                              onClick={() => {
                                this.setState({
                                  showTermsOfOrder: true,
                                });
                                window.analytics.track("Viewed Order Form");
                              }}
                            >
                              (View Order Form)
                            </div>
                          </Form.Group>
                        </Row>
                      )}
                      <Row className="mb-3">
                        <Form.Group
                          as={Col}
                          sm={12}
                          controlId="formBasicCRBAgreement"
                        >
                          <Form.Check
                            type="checkbox"
                            id="crbAgreement"
                            name="crbAgreement"
                            label="Cross River Bank Deposit Account Agreement"
                            value={this.state.crbAgreement as string}
                            onChange={this._handleCheckBoxChange}
                          />
                          <div
                            className="modal-link"
                            onClick={() =>
                              this.setState({ showCRBAgreement: true })
                            }
                          >
                            (View Agreement)
                          </div>
                        </Form.Group>
                      </Row>
                      <Row className="mb-3">
                        <Form.Group
                          as={Col}
                          sm={12}
                          controlId="formBasicFinalAgreementSpacing"
                        >
                          {" "}
                        </Form.Group>
                      </Row>
                      <Row className="mb-3">
                        <Form.Group
                          as={Col}
                          sm={12}
                          controlId="formBasicFinalAgreement"
                        >
                          <Form.Check
                            type="checkbox"
                            id="finalAgreement"
                            name="finalAgreement"
                            label="I Agree"
                            value={this.state.finalAgreement as string}
                            onChange={this._handleCheckBoxChange}
                          />
                          <p
                            style={{
                              fontStyle: "italic",
                              fontSize: 14,
                              paddingTop: 10,
                              paddingLeft: 4,
                              color: "#0a2540",
                            }}
                          >
                            By checking {'"I Agree"'} you are certifying that
                            you have read and agree to our
                            <a
                              href="https://iconsavingsplan.com/terms"
                              className="modal-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Terms of Service
                            </a>
                            {","}
                            <a
                              href="https://iconsavingsplan.com/privacy"
                              className="modal-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Privacy Policy
                            </a>
                            {", "}
                            {showBillingRouteAndTermsOfOrder && " Order Form, "}
                            and Platform Services Agreement.
                          </p>
                        </Form.Group>
                      </Row>

                      <div className="submit-row">
                        {this.props.error && (
                          <Alert type="error" msg={this.props.error} />
                        )}
                        <Button
                          disabled={this._calcButtonDisabled()}
                          withArrow={true}
                          name="submit"
                          btnLabel="Finish"
                          loading={this.props.isFetching}
                        />
                      </div>
                    </div>
                  </article>
                </section>

                <section>
                  <article className="plan-card-bottom">
                    <PlanCard planCode={this.props.planCode} />
                  </article>
                </section>
              </section>
            </div>
          </div>
        </Form>
      </div>
    );
  };
}

export default connector(withApollo<Props>(EmployerTerms));
